import { Intent } from "@blueprintjs/core";
import { addMinutes, format, parseJSON } from "date-fns";
import {
  ButtonContainer,
  DataTable,
  FGListen,
  FieldGroup,
  IDataTableColumn,
  showError,
  useGridState
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { DiplomeCertificationIfapmeDocumentEditDialog, DiplomeCertificationIfapmeDocumentUploadDialog } from ".";
import {
  AcquisScolaireHopeApi,
  DiplomeCertificationIfapmeEditDtoFromJSON,
  DiplomeCertificationIfapmeFichierGridDto
} from "../../../../../../api";
import {
  AddButton,
  DeleteButton,
  DownloadButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../../../../components";
import { useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";
import { exportFile } from "../../../../../../utils";

export interface IDiplomeCertificationIfapmeDetailProps {
  onTypeAcquisChange?: (typeAcquis: string) => void;
  idpersonne: number;
  listRoute: string;
}

export const DiplomeCertificationIfapmeDetail: React.FunctionComponent<IDiplomeCertificationIfapmeDetailProps> = ({
  onTypeAcquisChange,
  idpersonne,
  listRoute
}) => {
  const { t } = useTl();
  const api = useApiService(AcquisScolaireHopeApi);
  const [typeAcquis, taLoading] = useReferential(a => a.referentialGetAcquisScolaireTypes(), false, []);
  const [idtitreDiplomeCertificationIfapme, setIdtitreDiplomeCertificationIfapme] = React.useState(0);
  const [titreDiplomeCertification, tdcLoading] = useReferential(
    a => a.referentialGetTitreDiplomeCertificationsIfapme({ currentId: idtitreDiplomeCertificationIfapme }),
    true,
    [idtitreDiplomeCertificationIfapme]
  );
  const [anneeScolaires, asLoading] = useReferential(a => a.referentialGetAnneeScolaire(), false, []);
  const history = useHistory();
  const { state, acquisId } = useParams<{
    id: string;
    tab: string;
    state: string;
    acquisId: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);

  const { data, loading, deleteItem, deleting, saveItem, saving, validationErrors, refresh: refreshData } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          return +acquisId <= 0
            ? DiplomeCertificationIfapmeEditDtoFromJSON({
                iddiplomeCertificationIfapme: +acquisId,
                idpersonne: idpersonne,
                typeAcquis: "DiplomeCertification"
              })
            : api.acquisScolaireHopeGetDiplomeCertificationIfapme({ id: +acquisId });
        },
        saveApiFn: d => api.acquisScolaireHopeSaveDiplomeCertificationIfapmee({ DiplomeCertificationIfapmeEditDto: d }),
        onSavedRoute: d => listRoute,
        getDeps: [acquisId],
        deleteApiFn: d =>
          api.acquisScolaireHopeDeleteDiplomeCertificationIfapme({ id: d.iddiplomeCertificationIfapme }),
        onDeletedRoute: () => listRoute
      }),
      [acquisId, api, idpersonne, listRoute]
    )
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      typeAcquis: Yup.string().required(t(ETLCodes.Required)),
      date: Yup.date().typeError(t(ETLCodes.DateInvalide))
    });
  }, [t]);

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { uploadDate: "DESC", fileName: "ASC" }
  });

  const { setData } = tableState;

  React.useEffect(() => {
    if (data?.fichiers) {
      setData(data.fichiers);
    }
  }, [data?.fichiers, setData]);

  const [currentDiplomeCertificationIfapmeFichierId, setCurrentDiplomeCertificationIfapmeFichierId] = React.useState(
    null
  );
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (iddiplomeCertificationIfapmeFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(iddiplomeCertificationIfapmeFichier);
          await api.acquisScolaireHopeDeleteDiplomeCertificationIfapmeFichier({
            id: iddiplomeCertificationIfapmeFichier
          });
          refreshData();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refreshData, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (iddiplomeCertificationIfapmeFichier: number) => {
      try {
        setRowLoading(iddiplomeCertificationIfapmeFichier);
        const file = await api.acquisScolaireHopeDownloadDiplomeCertificationIfapmeFichier({
          id: iddiplomeCertificationIfapmeFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DiplomeCertificationIfapmeFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentDiplomeCertificationIfapmeFichierId(row.iddiplomeCertificationIfapmeFichier)}
              loading={rowLoading === row.iddiplomeCertificationIfapmeFichier}
              disabled={!!rowLoading && rowLoading !== row.iddiplomeCertificationIfapmeFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.iddiplomeCertificationIfapmeFichier)}
              loading={rowLoading === row.iddiplomeCertificationIfapmeFichier}
              disabled={!!rowLoading && rowLoading !== row.iddiplomeCertificationIfapmeFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.iddiplomeCertificationIfapmeFichier)}
              loading={rowLoading === row.iddiplomeCertificationIfapmeFichier}
              disabled={!!rowLoading && rowLoading !== row.iddiplomeCertificationIfapmeFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeAcquisFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const formatDate = React.useCallback((date: Date) => {
    return format(parseJSON(addMinutes(date, date.getTimezoneOffset())), "dd/MM/yyyy HH:mm");
  }, []);

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentDiplomeCertificationIfapmeFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        refreshData();
      }
    },
    [refreshData]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      showColons
      editMode={editMode}
      minLabelWidth={180}
      labelAlignment="right"
      formatDate="dd/MM/yyyy"
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(listRoute)}
      saving={saving}
      showDeleteButton={+acquisId > 0}
      onDelete={deleteItem}
      deleting={deleting}
      validationErrors={validationErrors}
      watchChanges={{
        acquis: setIdtitreDiplomeCertificationIfapme
      }}
    >
      <FieldGroup columns={2}>
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.DiplomeCertificationIfapme)
          }}
        >
          <FGListen field="typeAcquis" onChanged={onTypeAcquisChange}></FGListen>
          <FGWalterSelectInput
            label={t(ETLCodes.Type)}
            name="typeAcquis"
            items={typeAcquis}
            loading={taLoading}
            readonly={+(acquisId ?? 0) > 0}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.AnneeFormation)}
            name="anneeDeFormation"
            items={anneeScolaires}
            loading={asLoading}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.Acquis)}
            name="acquis"
            items={titreDiplomeCertification}
            loading={tdcLoading}
          />
          <FGWalterDateMaskInput label={t(ETLCodes.Date)} name="date" />
        </FieldGroup>
      </FieldGroup>
      <FieldGroup
        visible={+acquisId > 0}
        fieldsetProps={{
          title: t(ETLCodes.Documents),
          rightElement: (
            <AddButton
              onClick={e => {
                e.stopPropagation();
                onAdd();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
            />
          )
        }}
      >
        <DataTable
          dateFormat="dd/MM/yyyy HH:mm"
          formatDate={formatDate}
          tableState={tableState}
          loading={loading}
          columns={columns}
        ></DataTable>
        {uploadDialoOpen && (
          <DiplomeCertificationIfapmeDocumentUploadDialog
            onClose={onClose}
            dialogOpen={uploadDialoOpen}
            iddiplomeCertificationIfapme={+acquisId}
          />
        )}
        {!!currentDiplomeCertificationIfapmeFichierId && (
          <DiplomeCertificationIfapmeDocumentEditDialog
            onClose={onClose}
            iddiplomeCertificationIfapmeFichier={currentDiplomeCertificationIfapmeFichierId}
          />
        )}
      </FieldGroup>
    </SmallFormGenerator>
  );
};
